import { InstallationServiceData } from '@coolblue-development/next-installation-services';
import type { ClientPropositionProps } from '@coolblue-development/next-propositions/client';
import { ProductReviewsOverviewType } from '@coolblue-development/next-reviews/client';
import type { Collection } from '@features/product/components/Collection/Collection.types';
import { DistinctProperty, EnergyLabelType, Product, Reviews } from '@features/product/typings';
import { AdviceCueFragment } from '@features/product-page/api/fragments/adviceCue';

export type ProductPageLayoutVisitDimensions = {
  device: string;
  customerType: string;
  trafficSource: string;
  channel?: string;
};

export type Review = ProductReviewsOverviewType['reviews'][0];

type AlternativeDto = {
  product: ProductDto | null;
  distinctProperties: DistinctProperty[];
};

export type ProductDto = Omit<
  Product,
  | 'adviceCue'
  | 'alternatives'
  | 'description'
  | 'energyLabel'
  | 'generatedContent'
  | 'insurance'
  | 'mainText'
  | 'prosAndCons'
  | 'personalPrice'
  | 'reviewMetaData'
> & {
  alternatives: AlternativeDto[] | null;
  adviceCueAdvice: AdviceCueFragment['adviceCueAdvice'];
  adviceCueData: AdviceCueFragment['adviceCueData'];
  reviewMetaData: Omit<Reviews, 'images'> & {
    images: { id: string }[];
  };
  mainText: string | null;
  prosAndCons: {
    pros: string[] | null;
    cons: string[] | null;
  } | null;
  description: {
    versions: Array<{
      mainText: string | null;
      prosAndCons: {
        pros: string[] | null;
        cons: string[] | null;
      } | null;
      variation: string | null;
    }> | null;
  } | null;
  energyLabel: EnergyLabelType | null;
};

export type DeliveryPromiseMetaDataResponse = {
  deliveryMethod: 'TIMESLOT' | 'NEXTDAY' | 'TIMESPAN' | 'DIGITAL' | null;
};

export type DeliveryPromiseMethod = 'default' | 'timespan' | 'cbDelivers';
export type DeliveryPromise = {
  deliveryMethod: DeliveryPromiseMethod;
};

export type ProductPage = {
  customer: CustomerData | null;
  product: Product | null;
  hasAccessories: boolean;
  crossSellOnly: boolean;
  isComposedProduct: boolean;
  services: InstallationServiceData[] | null;
  productNavigation: ProductNavigation | null;
  productReviews: Review[];
  collectionByProductId: Collection | null;
  deliveryPromise: DeliveryPromise | null;
  customerAttributes: CustomerAttributes | null;
};

export type PropositionTypes = {
  header: ClientPropositionProps['propositions'];
  footer: ClientPropositionProps['propositions'];
  leaseProduct?: ClientPropositionProps['propositions'];
  product?: ClientPropositionProps['propositions'];
  subscriptionInformation: ClientPropositionProps['propositions'] | null;
};

export type ProductNavigation = {
  breadcrumbs: ProductNavigationNode[];
  topNavigation: ProductNavigationNode[];
  relevantSegments: ProductNavigationNode[];
};

export enum ProductContentType {
  CategoryPage = 'Category page',
  ProductTypeSegmentPage = 'Product type segment page',
  ProductTypePage = 'Product type page',
  ProductCollectionPage = 'Product Collection Page',
  BrandProductTypePage = 'Brand product type page'
}

export type ProductNavigationNode = {
  id?: string;
  title: string;
  url: string;
  urlIndexable: boolean | null;
  contentType: ProductContentType;
};

export type CustomerData = {
  id: string;
  emailAddress: string | null;
  closedCommunity: {
    id: string;
  } | null;
};

export interface CustomerAttributes {
  preferredStore: Store | null;
}

export type Store = {
  id: string;
  name: string;
};
